import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/** Acronyms that should not be title-cased when rendering a module heading. */
const MODULE_ACRONYMS: Record<string, string> = {
  hr: 'HR',
  ipd: 'IPD',
  opd: 'OPD',
  cms: 'CMS',
};

/** Human-readable label for a permission module key (e.g. `opd` → `OPD`). */
export function formatModuleLabel(module: string): string {
  const acronym = MODULE_ACRONYMS[module.toLowerCase()];
  if (acronym) return acronym;

  return module
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

/** Label for a permission category (sub-group inside a module), e.g. `payouts` → `Payouts`. */
export function formatCategoryLabel(category: string): string {
  return formatModuleLabel(category);
}

/**
 * Splits one module's permissions into its optional categories, preserving the
 * order the API returned (module › code). Un-categorised permissions stay first
 * so a module's primary list looks exactly as it did before categories existed.
 */
export function groupByCategory<T>(
  permissions: T[],
): { category?: string; permissions: T[] }[] {
  const order: string[] = [];
  const buckets = new Map<string, T[]>();
  const categoryOf = (perm: T) => (perm as { category?: string } | null)?.category ?? '';

  for (const perm of permissions) {
    const key = categoryOf(perm);
    if (!buckets.has(key)) {
      buckets.set(key, []);
      order.push(key);
    }
    buckets.get(key)!.push(perm);
  }

  return order
    .sort((a, b) => (a === '' ? -1 : b === '' ? 1 : 0))
    .map((key) => ({ category: key || undefined, permissions: buckets.get(key)! }));
}
